import React, { useState } from 'react';
import styled from 'styled-components';
import { OrderItem } from 'services';

interface Styles {
  cell?: React.CSSProperties;
  header?: React.CSSProperties;
  column?: React.CSSProperties;
}

interface FlexebleTableSchemaProps {
  header?: string;
  headerClick?(): void;
  cellClick?(item: any): void;
  mapper?(item: any): void;
  key: string;
  style?: Styles;
  cellHoverEnter?(item?: any): void;
  cellHoverLeave?(item?: any): void;
}

interface FlexebleTableProps {
  data?: any[];
  schema: FlexebleTableSchemaProps[];
  ordering?: OrderItem[];
  onRowClick?(item: any, index: number): void;
  onScroll?(): void;
  scrollThreshold?: number;
}

interface FlexebleTableState {
  hover: number;
  paginationDelay: null | Date;
}

const TableRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 20px;
  justify-content: space-between;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 100px;
`;

const TableColumn = styled.div`
  display: flex;
  width: max-content;
  flex-direction: column;
  flex: 1;
  white-space: nowrap;
`;

const HeaderCell = styled.div`
  font-weight: 600;
  cursor: pointer;
  margin: 0 2px 10px;
  user-select: none;
  padding: 5px 4px 10px;
`;

const TableCell = styled.div`
  min-height: 40px;
  padding: 5px 4px;
  position: relative;
  display: flex;
  align-items: center;

  &.hover {
    background: lightgrey;
    cursor: pointer;
  }

  &.cell-hover {
    cursor: pointer;
    &:hover {
      background: lightgrey;
    }
  }
`;

interface OrderingCarrotProps {
  direction: 'asc' | 'desc';
}

const OrderingCarrot = styled.span<OrderingCarrotProps>`
  display: inline-block;
  transform: rotate(${({ direction }) => (direction == 'asc' ? '90deg' : '-90deg')});
`;

export default function FlexebleTable({
  data,
  schema,
  ordering,
  onRowClick,
  onScroll,
  scrollThreshold,
}: FlexebleTableProps) {
  const [state, setState] = useState<FlexebleTableState>({
    hover: -1,
    paginationDelay: null,
  });
  const [scrollTrigger, setScrollTrigger] = useState<boolean>(false);

  const onScrollEvent = () => {
    if (!onScroll || !scrollThreshold) return;

    const clientRect = document.body.getBoundingClientRect();
    if (
      Math.abs(document.body.offsetHeight + clientRect.top - window.innerHeight) <= scrollThreshold
    ) {
      if (
        !state.paginationDelay ||
        (state.paginationDelay &&
          (new Date().getTime() - state.paginationDelay.getTime()) / 1000 >= 0.2)
      ) {
        setState({ ...state, paginationDelay: new Date() });
        onScroll();
      }
    }
  };

  React.useEffect(() => {
    if (!onScroll) return;

    let localScrollTrigger = false;
    const scrollTriggerHandler = () => {
      localScrollTrigger = !localScrollTrigger;
      setScrollTrigger(localScrollTrigger);
    };

    window.addEventListener('scroll', scrollTriggerHandler);

    return () => {
      window.removeEventListener('scroll', scrollTriggerHandler);
    };
  }, []);

  React.useEffect(() => onScrollEvent(), [scrollTrigger]);

  return (
    <TableRow onMouseLeave={() => setState({ ...state, hover: -1 })}>
      {schema.map((schema_item, index) => (
        <TableColumn
          key={`column_${schema_item?.header ?? index}`}
          style={schema_item?.style?.column}
        >
          {schema_item?.header ? (
            <HeaderCell onClick={schema_item?.headerClick} style={schema_item?.style?.header}>
              {schema_item?.header}

              {ordering?.find((oitem) => oitem?.field === schema_item?.key) && (
                <OrderingCarrot
                  direction={
                    ordering?.find((oitem) => oitem?.field === schema_item?.key)?.type ?? 'asc'
                  }
                >
                  &#8227;
                </OrderingCarrot>
              )}
            </HeaderCell>
          ) : (
            <></>
          )}
          {data?.map((data_item, index) => (
            <TableCell
              style={schema_item?.style?.cell}
              className={`${onRowClick && state?.hover == index ? 'hover' : ''} ${
                schema_item?.cellClick ? 'cell-hover' : ''
              }`}
              key={`row_${index}_${(data_item?.id || data_item?.uid) ?? ''}`}
              onClick={() => {
                if (schema_item?.cellClick) schema_item?.cellClick(data_item);

                if (onRowClick) onRowClick(data_item, index);
              }}
              onMouseEnter={() => {
                if (onRowClick) setState({ ...state, hover: index });
                if (schema_item?.cellHoverEnter) schema_item?.cellHoverEnter(data_item);
              }}
              onMouseLeave={() => {
                if (schema_item?.cellHoverLeave) schema_item?.cellHoverLeave(data_item);
              }}
            >
              {schema_item?.mapper ? schema_item?.mapper(data_item) : data_item?.[schema_item?.key]}
            </TableCell>
          ))}
        </TableColumn>
      ))}
    </TableRow>
  );
}
